import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { Box, Typography, CircularProgress, Fab, Fade, alpha, useTheme } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Virtuoso } from "react-virtuoso";
import { useTranslation } from "react-i18next";
import TeamMessageBubble from "./TeamMessageBubble";
import TaskActivityLogLine, {
  isTeamActivityLogMessage,
} from "../../TeamTasks/components/TaskActivityLogLine";
import { dayKey, formatDayLabel } from "../utils/teamUiUtils";
import { consumeTeamScrollToMessage } from "../utils/teamMessageUtils";
import { shouldHideTeamVideoMessage } from "../../../components/MessageHistory/VideoCallSummaryMessage";

const INITIAL_FIRST_ITEM_INDEX = 100_000;
const LIST_BOTTOM_SPACER_PX = 24;
const QUOTED_SCROLL_MAX_LOAD_ATTEMPTS = 8;
const QUOTED_HIGHLIGHT_MS = 2200;

const ListFooter = () => <div style={{ height: LIST_BOTTOM_SPACER_PX, flexShrink: 0 }} />;

/** Rolagem vertical nativa; bloqueia overflow horizontal (ações flutuantes / imagens largas). */
const TeamVirtuosoScroller = forwardRef(function TeamVirtuosoScroller(
  { style, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      {...props}
      style={{
        ...style,
        overflowX: "hidden",
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    />
  );
});

function buildTimelineItems(messages) {
  const source = messages || [];
  const visible = source.filter((msg) => !shouldHideTeamVideoMessage(msg, source));
  const items = [];
  for (let i = 0; i < visible.length; i += 1) {
    const msg = visible[i];
    const prev = visible[i - 1];
    if (i === 0 || dayKey(prev?.createdAt) !== dayKey(msg.createdAt)) {
      items.push({
        kind: "day",
        key: `day-${dayKey(msg.createdAt)}`,
        label: formatDayLabel(msg.createdAt),
      });
    }
    items.push({
      kind: "message",
      key: `msg-${msg.id}`,
      msg,
      prev: prev || null,
    });
  }
  return items;
}

const TeamMessageList = ({
  messages,
  currentUserId,
  onLoadMore,
  hasMore,
  isLoading,
  loadError = false,
  onQuickReply,
  isSending = false,
  onReply,
  onEdit,
  onDelete,
  onForward,
  onReact,
  spaceType = "channel",
  onAudioPlayed,
  listKey,
  spaceId = null,
  spaceLeadId = null,
  spaceCardId = null,
  onOpenTask = null,
  onNavigateToSpace,
  compact = false,
}) => {
  const { t } = useTranslation("team");
  const theme = useTheme();
  const virtuosoRef = useRef(null);
  const [clearedInteractiveIds, setClearedInteractiveIds] = useState(() => new Set());
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_FIRST_ITEM_INDEX);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const timelineSnapshotRef = useRef({ firstId: null, len: 0 });
  const pendingQuotedScrollRef = useRef({ messageId: null, attempts: 0 });
  const highlightTimerRef = useRef(null);

  const listBgSx = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      bgcolor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.default, 0.45)
          : "#e9edef",
      backgroundImage:
        theme.palette.mode === "dark"
          ? "none"
          : `radial-gradient(${alpha(theme.palette.primary.main, 0.04)} 1px, transparent 1px)`,
      backgroundSize: theme.palette.mode === "dark" ? undefined : "18px 18px",
    }),
    [theme]
  );

  const timelineItems = useMemo(() => buildTimelineItems(messages), [messages]);

  const lastMessageKey = useMemo(() => {
    for (let i = timelineItems.length - 1; i >= 0; i -= 1) {
      if (timelineItems[i].kind === "message") return timelineItems[i].key;
    }
    return null;
  }, [timelineItems]);

  useEffect(() => {
    setFirstItemIndex(INITIAL_FIRST_ITEM_INDEX);
    setInitialScrollDone(false);
    setIsAtBottom(true);
    timelineSnapshotRef.current = { firstId: null, len: 0 };
  }, [listKey]);

  useEffect(() => {
    const firstId = messages[0]?.id ?? null;
    const len = timelineItems.length;
    const snap = timelineSnapshotRef.current;

    if (
      snap.firstId != null &&
      firstId != null &&
      firstId < snap.firstId &&
      len > snap.len
    ) {
      setFirstItemIndex((idx) => idx - (len - snap.len));
    }

    timelineSnapshotRef.current = { firstId, len };
  }, [messages, timelineItems.length]);

  useEffect(() => {
    if (!initialScrollDone && timelineItems.length > 0) {
      let cancelled = false;
      const id1 = requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled || !virtuosoRef.current) return;
          virtuosoRef.current.scrollToIndex({
            index: timelineItems.length - 1,
            align: "end",
            behavior: "auto",
          });
          setInitialScrollDone(true);
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id1);
      };
    }
    return undefined;
  }, [timelineItems.length, initialScrollDone, listKey]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (!virtuosoRef.current || !timelineItems.length) return;
    virtuosoRef.current.scrollToIndex({
      index: timelineItems.length - 1,
      align: "end",
      behavior,
    });
  }, [timelineItems.length]);

  const findMessageTimelineIndex = useCallback(
    (targetId) => {
      const tid = Number(targetId);
      if (!Number.isFinite(tid)) return -1;
      return timelineItems.findIndex(
        (item) => item.kind === "message" && Number(item.msg?.id) === tid
      );
    },
    [timelineItems]
  );

  const flashHighlightedMessage = useCallback((messageId) => {
    if (highlightTimerRef.current) {
      window.clearTimeout(highlightTimerRef.current);
    }
    setHighlightedMessageId(Number(messageId));
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedMessageId(null);
      highlightTimerRef.current = null;
    }, QUOTED_HIGHLIGHT_MS);
  }, []);

  const scrollToMessageId = useCallback(
    (targetId, { tryLoadOlder = true } = {}) => {
      const tid = Number(targetId);
      if (!Number.isFinite(tid)) return false;

      const idx = findMessageTimelineIndex(tid);
      if (idx >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: idx,
          align: "center",
          behavior: "smooth",
        });
        flashHighlightedMessage(tid);
        pendingQuotedScrollRef.current = { messageId: null, attempts: 0 };
        return true;
      }

      if (
        tryLoadOlder &&
        hasMore &&
        typeof onLoadMore === "function" &&
        pendingQuotedScrollRef.current.attempts < QUOTED_SCROLL_MAX_LOAD_ATTEMPTS
      ) {
        const pending = pendingQuotedScrollRef.current;
        pendingQuotedScrollRef.current = {
          messageId: tid,
          attempts: pending.messageId === tid ? pending.attempts + 1 : 1,
        };
        onLoadMore();
      }

      return false;
    },
    [findMessageTimelineIndex, flashHighlightedMessage, hasMore, onLoadMore]
  );

  const handleNavigateToQuotedMessage = useCallback(
    (target) => {
      const messageId =
        target != null && typeof target === "object" ? target.messageId : target;
      const targetSpaceId =
        target != null && typeof target === "object" ? target.spaceId : null;

      if (messageId == null) return;

      if (
        targetSpaceId != null &&
        spaceId != null &&
        Number(targetSpaceId) !== Number(spaceId)
      ) {
        onNavigateToSpace?.(targetSpaceId, messageId);
        return;
      }

      scrollToMessageId(messageId);
    },
    [onNavigateToSpace, scrollToMessageId, spaceId]
  );

  useEffect(() => {
    const pendingId = pendingQuotedScrollRef.current.messageId;
    if (!pendingId) return;
    const found = scrollToMessageId(pendingId, { tryLoadOlder: true });
    if (!found && !hasMore) {
      pendingQuotedScrollRef.current = { messageId: null, attempts: 0 };
    }
  }, [messages, hasMore, scrollToMessageId]);

  useEffect(() => {
    if (!listKey || !timelineItems.length) return undefined;
    const pendingMessageId = consumeTeamScrollToMessage(listKey);
    if (!pendingMessageId) return undefined;

    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (!cancelled) scrollToMessageId(pendingMessageId, { tryLoadOlder: true });
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [listKey, timelineItems.length, scrollToMessageId]);

  useEffect(
    () => () => {
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    },
    []
  );

  const handleQuickReply = useCallback(
    async (messageId, label) => {
      if (!label || !onQuickReply) return;
      setClearedInteractiveIds((prev) => new Set(prev).add(messageId));
      await onQuickReply(label);
    },
    [onQuickReply]
  );

  const ListHeader = useCallback(() => {
    if (!hasMore && !isLoading) return null;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5, px: 2 }}>
        {isLoading ? (
          <CircularProgress size={22} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("loadOlder")}
          </Typography>
        )}
      </Box>
    );
  }, [hasMore, isLoading, t]);

  const renderItem = useCallback(
    (_index, item) => {
      if (item.kind === "day") {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", my: compact ? 0.75 : 1.5, px: compact ? 0.5 : { xs: 1.25, sm: 2 } }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "0.75rem",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                color: "text.secondary",
                bgcolor: (th) =>
                  th.palette.mode === "dark"
                    ? alpha(th.palette.common.white, 0.08)
                    : alpha(th.palette.common.white, 0.92),
                boxShadow: (th) =>
                  th.palette.mode === "dark"
                    ? "none"
                    : `0 1px 1px ${alpha(th.palette.common.black, 0.06)}`,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      }

      if (isTeamActivityLogMessage(item.msg)) {
        return (
          <Box
            sx={{
              px: compact ? 0.5 : { xs: 1.25, sm: 2 },
              pb: item.key === lastMessageKey ? (compact ? 0.25 : 0.5) : 0,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <TaskActivityLogLine
              text={item.msg?.content || ""}
              actorName={item.msg?.authorName || ""}
              createdAt={item.msg?.createdAt}
              compact
            />
          </Box>
        );
      }

      return (
        <Box
          sx={{
            px: compact ? 0.5 : { xs: 1.25, sm: 2 },
            pb: item.key === lastMessageKey ? (compact ? 0.5 : 1) : 0,
            minWidth: 0,
            maxWidth: "100%",
            borderRadius: 2,
            ...(highlightedMessageId != null &&
            Number(item.msg?.id) === Number(highlightedMessageId)
              ? {
                  animation: "teamQuotedHighlight 2.2s ease-out",
                  "@keyframes teamQuotedHighlight": {
                    "0%, 18%": {
                      bgcolor: alpha(theme.palette.warning.main, 0.28),
                    },
                    "100%": { bgcolor: "transparent" },
                  },
                }
              : {}),
          }}
        >
          <TeamMessageBubble
            msg={item.msg}
            prev={item.prev}
            currentUserId={currentUserId}
            isSending={isSending}
            onQuickReply={handleQuickReply}
            clearedInteractiveIds={clearedInteractiveIds}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onForward={onForward}
            onReact={onReact}
            spaceType={spaceType}
            onAudioPlayed={onAudioPlayed}
            onNavigateToQuotedMessage={handleNavigateToQuotedMessage}
            currentSpaceId={spaceId}
            spaceLeadId={spaceLeadId}
            spaceCardId={spaceCardId}
            onOpenTask={onOpenTask}
          />
        </Box>
      );
    },
    [
      clearedInteractiveIds,
      currentUserId,
      handleQuickReply,
      isSending,
      onAudioPlayed,
      onEdit,
      onDelete,
      onForward,
      onReact,
      onReply,
      spaceType,
      spaceId,
      spaceLeadId,
      spaceCardId,
      onOpenTask,
      handleNavigateToQuotedMessage,
      highlightedMessageId,
      theme,
      lastMessageKey,
      compact,
    ]
  );

  const virtuosoComponents = useMemo(
    () => ({
      Header: ListHeader,
      Footer: ListFooter,
      Scroller: TeamVirtuosoScroller,
    }),
    [ListHeader]
  );

  if (isLoading && !messages.length) {
    return (
      <Box
        className="team-message-scroll"
        sx={{
          ...listBgSx,
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: compact ? 4 : 8,
          px: compact ? 1.5 : 2,
        }}
      >
        <CircularProgress size={compact ? 24 : 28} />
      </Box>
    );
  }

  if (!messages.length && !isLoading) {
    return (
      <Box
        className="team-message-scroll"
        sx={{
          ...listBgSx,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: compact ? 4 : 8,
          px: compact ? 1.5 : 2,
        }}
      >
        <ForumOutlinedIcon sx={{ fontSize: compact ? 36 : 48, color: "text.disabled", mb: 1.5 }} />
        <Typography variant={compact ? "body2" : "subtitle1"} fontWeight={600} gutterBottom>
          {loadError
            ? t("messagesLoadErrorTitle")
            : compact
              ? t("sidebarTeamEmptyTitle")
              : t("emptyThreadTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: compact ? 260 : 360, lineHeight: 1.55 }}>
          {loadError
            ? t("messagesLoadErrorHint")
            : compact
              ? t("sidebarTeamEmptyHint")
              : t("emptyMessages")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...listBgSx,
        position: "relative",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      className="team-message-scroll"
    >
      <Virtuoso
        key={listKey}
        ref={virtuosoRef}
        data={timelineItems}
        firstItemIndex={firstItemIndex}
        computeItemKey={(_index, item) => item.key}
        initialTopMostItemIndex={Math.max(0, timelineItems.length - 1)}
        atBottomThreshold={80}
        followOutput={(atBottom) => (atBottom ? "smooth" : false)}
        startReached={() => {
          if (hasMore && !isLoading && typeof onLoadMore === "function") {
            onLoadMore();
          }
        }}
        components={virtuosoComponents}
        itemContent={renderItem}
        atBottomStateChange={setIsAtBottom}
        style={{ height: "100%", width: "100%" }}
      />

      <Fade in={!isAtBottom && timelineItems.length > 0}>
        <Fab
          color="primary"
          size="small"
          onClick={() => scrollToBottom("smooth")}
          aria-label={t("scrollToBottom", "Ir para mensagens recentes")}
          sx={{
            position: "absolute",
            bottom: compact ? 8 : 16,
            right: compact ? 8 : 16,
            zIndex: 2,
            transform: compact ? "scale(0.85)" : undefined,
          }}
        >
          <ArrowDownwardIcon />
        </Fab>
      </Fade>
    </Box>
  );
};

export default TeamMessageList;
